// React
import React, { useState } from 'react';

// Packages
import cc from 'classcat';
import t from 'prop-types';
import AnimateHeight from 'react-animate-height';
import { motion, AnimatePresence } from 'framer-motion';
import sanitizeHtml from 'sanitize-html';

// Utilities
import { useLabels, useElseware, useFormat, useUser } from 'utilities/hooks';
import { useAssistedInteractionsDataStore } from 'utilities/store';

// Components
import Button from 'components/button';
import Spinner from 'components/spinner';

// Icons
import { FiChevronUp, FiChevronDown, FiCopy } from 'react-icons/fi';
import { LuSparkle } from 'react-icons/lu';

// Images
import FGLogoMark from 'assets/images/fg-logo-mark-small.svg';

const DropDownBaseCardComponent = ({
    title,
    description,
    relation,
    icon: Icon,
    promptKey,
    systemPrompts,
    promptDataGetter,
    forceOpen,
}) => {
    // ///////////////////
    // STORES
    // ///////////////////

    const { utilities } = useAssistedInteractionsDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label } = useLabels();
    const { ewCreate, ewUpdate, ewPostNoToken } = useElseware();
    const { dateTime } = useFormat();
    const { user } = useUser();

    // ///////////////////
    // STATE
    // ///////////////////

    const [expandedContent, setExpandedContent] = useState(forceOpen);
    const [latestInteraction, setLatestInteraction] = useState(
        utilities.interactions
            .getFromPromptKeyAndRelationId(
                promptKey,
                relation?.key,
                relation?.id
            )
            .filter(item => item?.CreatedById === user?.Id)
            .sort((a, b) => {
                return new Date(b?.CreatedDate) - new Date(a?.CreatedDate);
            })[0] || null
    );
    const [loading, setLoading] = useState(false);

    // ///////////////////
    // METHODS
    // ///////////////////

    async function askAi() {
        setLoading(true);
        try {
            // Get prompt data
            const promptData = await promptDataGetter();

            // Latest interaction
            let interaction;

            const answer = await ewPostNoToken('ai/foundgood-assistant', {
                type: 'prompt',
                systemPrompts,
                userPrompts: [
                    ...prompt?.Prompt__c?.split(';'),
                    `Data: ${promptData}`,
                ],
            });

            interaction = { Response__c: answer };

            if (relation?.key && relation?.id) {
                // Define the interaction data
                const interactionData = {
                    AI_Prompt__c: prompt?.Id,
                    [relation.key]: relation.id,
                    Response__c: answer,
                };

                // Check if there is an existing interaction
                let itemData = null;
                if (latestInteraction?.Id) {
                    // Update existing interaction
                    itemData = await ewUpdate(
                        'ai-interaction/ai-interaction',
                        latestInteraction.Id,
                        interactionData
                    );

                    interaction = itemData?.data;
                } else {
                    // Create new interaction
                    itemData = await ewCreate(
                        'ai-interaction/ai-interaction',
                        interactionData
                    );

                    interaction = itemData?.data;
                }
            }

            // Update latest interaction
            setLatestInteraction(interaction);

            // Update main data
            utilities.updateAIData('_interactions', interaction);

            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.warn(error);
        }
    }

    async function copyToClipboard(string) {
        try {
            await navigator.clipboard.writeText(
                sanitizeHtml(string, {
                    allowedTags: [],
                    allowedAttributes: {},
                    allowedIframeHostnames: [],
                })
            );
        } catch (error) {
            console.warn(error);
        }
    }

    // ///////////////////
    // DATA
    // ///////////////////

    // Get prompt data
    const prompt = utilities.prompts.getFromPromptKey(promptKey);

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <div
            className={cc([
                'p-16 w-full rounded-8 text-amber-100 transition-default bg-white shadow-md',
            ])}>
            {/* Content wrapper */}
            <div className="flex flex-col space-y-16 md:space-x-16 md:space-y-0 md:flex-row">
                {/* Image wrapper */}
                <div className="flex relative flex-shrink-0 bg-amber-200 items-center justify-center overflow-hidden h-76 md:h-120 w-full md:max-w-[120px] rounded-4">
                    <Icon className="w-48 h-48 text-white" />
                </div>
                <div className="flex w-full gap-16">
                    {/* Title wrapper */}
                    <div className="flex justify-between max-w-[600px]">
                        <div className="flex flex-col justify-center">
                            {/* Card preTitle */}
                            <div className="flex flex-col mb-6 t-footnote text-amber-60">
                                {relation.type && (
                                    <span>
                                        {label('AI_PromptCard_RelatedTo')}{' '}
                                        {relation.type}
                                    </span>
                                )}
                                {relation.name && (
                                    <span className="t-footnote-bold">
                                        {relation.name}
                                    </span>
                                )}
                            </div>

                            {/* Title wrapper */}
                            <div className="flex mb-4 text-amber-100 break-keep t-h5">
                                {title}
                            </div>

                            {/* Card postTitle */}
                            {description && (
                                <div className="t-caption text-amber-100 !-mt-2">
                                    {description}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Card controls */}
                    <div className="flex flex-col order-2 !ml-auto">
                        <Button
                            title={label('ButtonExpandCollapse')}
                            variant="secondary"
                            theme="amber"
                            icon={expandedContent ? FiChevronUp : FiChevronDown}
                            iconPosition="center"
                            iconType="stroke"
                            className="!px-8"
                            action={() => setExpandedContent(!expandedContent)}
                        />
                    </div>
                </div>
            </div>
            <AnimateHeight duration={300} height={expandedContent ? 'auto' : 0}>
                <div className="md:px-40 md:pl-[136px] md:pr-56 mt-24">
                    <AnimatePresence>
                        {latestInteraction ? (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{
                                    y: 5,
                                    opacity: 0,
                                    transition: { duration: 0.2 },
                                }}
                                className="relative mb-48 bg-amber-20 rounded-2xl shadow-md md:max-w-[600px] p-16 flex flex-col gap-16">
                                {/* Foundgood Icon */}
                                <div className="absolute bottom-0 flex-col items-center justify-center hidden text-white rounded-full shadow-md md:flex w-36 h-36 t-footnote -left-46 bg-amber-200">
                                    <FGLogoMark
                                        className="w-16 text-amber-100"
                                        fill="currentColor"
                                    />
                                </div>

                                {/* Date stamp */}
                                <p className="absolute -bottom-[28px] t-footnote text-amber-60">
                                    {label('AI_PromptCard_GenerationDate')}{' '}
                                    {dateTime(
                                        latestInteraction?.LastModifiedDate
                                    )}
                                </p>

                                {/* Answer */}
                                <p
                                    className="flex flex-col gap-8 t-caption"
                                    dangerouslySetInnerHTML={{
                                        __html: latestInteraction?.Response__c,
                                    }}
                                />

                                {/* Tooling */}
                                <div className="flex flex-wrap items-center gap-16 gap-y-8">
                                    <Button
                                        size="tiny"
                                        variant="tertiary"
                                        icon={FiCopy}
                                        iconPosition="left"
                                        action={() =>
                                            copyToClipboard(
                                                latestInteraction?.Response__c
                                            )
                                        }
                                        iconType="stroke"
                                        theme="amber">
                                        {label('ButtonCopy')}
                                    </Button>
                                    <Button
                                        size="tiny"
                                        icon={LuSparkle}
                                        iconPosition="left"
                                        iconType="fill"
                                        disabled={loading}
                                        action={() => askAi()}
                                        variant="primary"
                                        theme="amber">
                                        {label('ButtonRegenerate')}
                                    </Button>
                                    {loading && <Spinner theme="amber" />}
                                </div>
                            </motion.div>
                        ) : (
                            <div className="flex flex-col items-center justify-center space-y-16">
                                {loading ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{
                                            y: 5,
                                            opacity: 0,
                                            transition: { duration: 0.2 },
                                        }}
                                        className="p-32">
                                        <Spinner theme="amber" />
                                    </motion.div>
                                ) : (
                                    <>
                                        <p className="t-body">
                                            {label('AI_PromptCard_AskAI')}
                                        </p>
                                        <Button
                                            title={label(
                                                'ButtonExpandCollapse'
                                            )}
                                            variant="primary"
                                            theme="amber"
                                            icon={LuSparkle}
                                            iconPosition="left"
                                            iconType="fill"
                                            action={() => askAi()}>
                                            Generate {title}
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                    </AnimatePresence>
                </div>
            </AnimateHeight>
        </div>
    );
};

DropDownBaseCardComponent.propTypes = {
    title: t.string.isRequired,
    description: t.string,
    relation: t.shape({
        key: t.string.isRequired,
        id: t.string.isRequired,
        name: t.string.isRequired,
        type: t.string.isRequired,
    }).isRequired,
    icon: t.elementType.isRequired,
    promptKey: t.string.isRequired,
    systemPrompts: t.arrayOf(t.string),
    forceOpen: t.bool,
};

DropDownBaseCardComponent.defaultProps = {
    type: '',
    description: '',
    relation: {
        key: '',
        id: '',
        name: '',
        type: '',
    },
    icon: LuSparkle,
    promptKey: '',
    systemPrompts: [],
    forceOpen: false,
};

export default DropDownBaseCardComponent;
